import React, { useState } from "react";
import { Button, Menu, MenuItem, ListItemText, Divider, makeStyles } from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { Link as RouterLink } from "react-router-dom";

/**
 * One action inside the row's overflow menu.
 *
 * - `to` makes it a router link (Orgs / Configure).
 * - `onClick` makes it a regular menu item (Delete / Deactivate / etc.).
 * - `dangerous` colours the label red so destructive ops stand out without
 *   inventing a separate component.
 * - `dividerBefore` inserts a thin separator above this item — used to
 *   visually group safe actions vs destructive ones.
 */
export type RowAction = {
    label: string;
    to?: string;
    onClick?: () => void;
    dangerous?: boolean;
    disabled?: boolean;
    dividerBefore?: boolean;
    hidden?: boolean;
};

export const RowActionsMenu: React.FC<{
    actions: RowAction[];
    ariaLabel?: string;
    /** Trigger button label. Defaults to "Actions". */
    label?: string;
}> = ({
    actions,
    ariaLabel = "Row actions",
    label = "Actions",
}) => {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const visibleActions = actions.filter((a) => !a.hidden);
    if (visibleActions.length === 0) return null;

    const close = () => setAnchorEl(null);

    return (
        <>
            <Button
                size="small"
                variant="outlined"
                color="primary"
                aria-label={ariaLabel}
                aria-haspopup="menu"
                aria-expanded={!!anchorEl}
                onClick={(e) => setAnchorEl(e.currentTarget as HTMLElement)}
                className={classes.trigger}
            >
                {label}
                <ArrowDropDownIcon fontSize="small" className={classes.triggerIcon} />
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={close}
                keepMounted
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                {visibleActions.flatMap((action, idx) => {
                    const itemClass = action.dangerous ? classes.dangerous : undefined;
                    const item = action.to ? (
                        <MenuItem
                            key={action.label}
                            component={RouterLink as React.ElementType}
                            to={action.to}
                            disabled={action.disabled}
                            onClick={close}
                            className={itemClass}
                        >
                            <ListItemText primary={action.label} />
                        </MenuItem>
                    ) : (
                        <MenuItem
                            key={action.label}
                            disabled={action.disabled}
                            onClick={() => {
                                close();
                                action.onClick?.();
                            }}
                            className={itemClass}
                        >
                            <ListItemText primary={action.label} />
                        </MenuItem>
                    );
                    return action.dividerBefore && idx > 0
                        ? [<Divider key={`d-${action.label}`} />, item]
                        : [item];
                })}
            </Menu>
        </>
    );
};

const useStyles = makeStyles((theme) => ({
    trigger: {
        whiteSpace: 'nowrap',
    },
    triggerIcon: {
        marginLeft: '4px',
    },
    dangerous: {
        color: theme.palette.error.main,
    },
}));
